import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, TrendingUp, Minus } from "lucide-react";
import { getUser, clearAuth, authFetch } from "@/lib/auth";
import AuthModal from "@/components/AuthModal";
import { PERSONA_META, DOMAIN_ORDER, useLang } from "@/components/landing/i18n";

export default function ComparePage() {
  const { lang } = useLang();
  const [user, setUser] = useState(getUser());
  const [authOpen, setAuthOpen] = useState(false);
  const [reports, setReports] = useState(null);

  useEffect(() => {
    if (!user) return;
    authFetch("/api/my/reports").then(async (r) => {
      if (r.status === 401) {
        clearAuth();
        setUser(null);
        return;
      }
      setReports(await r.json());
    });
  }, [user]);

  const latest = (track) => reports?.find((r) => (r.track || "student") === track);
  const stu = latest("student");
  const pro = latest("professional");
  const both = stu && pro;

  const shift = both ? DOMAIN_ORDER.map((k) => ({ k, d: (pro.scores[k] || 0) - (stu.scores[k] || 0) })).sort((a, b) => b.d - a.d) : [];
  const gained = shift.filter((s) => s.d > 0);
  const dropped = shift.filter((s) => s.d < 0).reverse();

  const Card = ({ r, label, accent }) => {
    const meta = PERSONA_META[r.persona];
    const txt = meta[lang] || meta.en;
    return (
      <div className="rounded-3xl bg-cream border border-ink/10 p-7" data-testid={`compare-card-${label.toLowerCase().split(" ")[0]}`}>
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: accent }}>{label}</div>
        <div className="font-display font-bold text-3xl tracking-tight mt-3" style={{ color: meta.color }}>{txt.name}</div>
        <div className="mt-2 inline-flex rounded-full bg-ink text-paper px-3.5 py-1 text-xs font-semibold">Aptitude {r.aptPct}%</div>
        <div className="text-xs text-ink/45 mt-3">{new Date(r.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</div>
        <div className="mt-5 space-y-2">
          {DOMAIN_ORDER.map((k) => (
            <div key={k} className="flex items-center gap-2.5">
              <span className="w-24 text-[10px] font-semibold text-ink/55 text-right truncate">{PERSONA_META[k][lang]?.name || PERSONA_META[k].en.name}</span>
              <div className="flex-1 h-2 rounded-full bg-ink/8 overflow-hidden">
                <motion.div className="h-full rounded-full" style={{ background: PERSONA_META[k].color }} initial={{ width: 0 }} animate={{ width: `${((r.scores[k] || 0) / 12) * 100}%` }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} />
              </div>
              <span className="w-5 text-[11px] font-bold" style={{ color: PERSONA_META[k].color }}>{r.scores[k] || 0}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-paper text-ink font-body" data-testid="compare-page">
      <header className="max-w-5xl mx-auto px-5 pt-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="compare-home-logo">
          <img src="/brightiq-logo.png" alt="Bright IQ" className="h-10 w-10 rounded-lg object-contain" />
          <span className="font-display font-bold text-lg tracking-tight">Bright<span className="text-iqblue">IQ</span></span>
        </Link>
        <Link to="/reports" className="text-sm font-semibold text-ink/60 hover:text-ink transition-colors" data-testid="compare-back-link">My reports</Link>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-14">
        {!user && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="compare-gate">
            <h1 className="font-display font-bold text-3xl tracking-tight">Sign in to compare your batteries</h1>
            <button onClick={() => setAuthOpen(true)} data-testid="compare-signin-button" className="mt-8 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors duration-300">
              Sign in / Create free account
            </button>
          </div>
        )}

        {user && !reports && <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-iqblue" data-testid="compare-loading" /></div>}

        {user && reports && !both && (
          <div className="rounded-[2rem] bg-cream border border-ink/10 p-10 text-center" data-testid="compare-empty">
            <h1 className="font-display font-bold text-3xl tracking-tight">Take both batteries to compare</h1>
            <p className="mt-3 text-ink/60 text-sm max-w-md mx-auto">
              {stu ? "You've completed the Student battery — now take the Career Professional one." : pro ? "You've completed the Professional battery — now take the Student one." : "You haven't completed a full battery yet."}
            </p>
            <Link to="/assessment" data-testid="compare-start-link" className="mt-8 inline-flex items-center gap-2 bg-ink text-paper font-semibold px-8 py-4 rounded-full hover:bg-iqblue transition-colors">
              {stu || pro ? "Take the other battery" : "Start a battery"} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {user && both && (
          <>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-center" data-testid="compare-heading">Then vs now</h1>
            <p className="mt-3 text-ink/60 text-sm sm:text-base text-center max-w-lg mx-auto">How your classroom self and your working-self persona line up.</p>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              <Card r={stu} label="Student battery" accent="#1F5BFF" />
              <Card r={pro} label="Professional battery" accent="#7C3AED" />
            </div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="mt-8 rounded-3xl bg-ink text-paper p-8 sm:p-10" data-testid="compare-verdict">
              <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-iqcyan">The shift</div>
              <p className="mt-4 font-display text-xl sm:text-2xl font-bold tracking-tight leading-snug">
                {stu.persona === pro.persona
                  ? `Steady signal — you're ${PERSONA_META[pro.persona][lang]?.name || PERSONA_META[pro.persona].en.name} in both worlds.`
                  : <>School saw <span style={{ color: PERSONA_META[stu.persona].color }}>{PERSONA_META[stu.persona][lang]?.name || PERSONA_META[stu.persona].en.name}</span>; work brings out <span style={{ color: PERSONA_META[pro.persona].color }}>{PERSONA_META[pro.persona][lang]?.name || PERSONA_META[pro.persona].en.name}</span>.</>}
              </p>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-iqgreen text-xs font-bold uppercase tracking-wider"><TrendingUp className="h-3.5 w-3.5" /> Stronger at work</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {gained.length ? gained.map((s) => (
                      <span key={s.k} className="text-xs font-semibold rounded-full px-3 py-1.5" style={{ background: `${PERSONA_META[s.k].color}22`, color: PERSONA_META[s.k].color }}>
                        {PERSONA_META[s.k][lang]?.name || PERSONA_META[s.k].en.name} +{s.d}
                      </span>
                    )) : <span className="text-xs text-paper/50 flex items-center gap-1"><Minus className="h-3 w-3" /> No big jumps</span>}
                  </div>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <div className="flex items-center gap-2 text-iqpink text-xs font-bold uppercase tracking-wider"><Minus className="h-3.5 w-3.5" /> Quieter at work</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {dropped.length ? dropped.map((s) => (
                      <span key={s.k} className="text-xs font-semibold rounded-full px-3 py-1.5" style={{ background: `${PERSONA_META[s.k].color}22`, color: PERSONA_META[s.k].color }}>
                        {PERSONA_META[s.k][lang]?.name || PERSONA_META[s.k].en.name} {s.d}
                      </span>
                    )) : <span className="text-xs text-paper/50 flex items-center gap-1"><Minus className="h-3 w-3" /> Nothing dropped</span>}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </main>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={(u) => setUser(u)} />
    </div>
  );
}
